import { Checkbox, ListItemText, MenuItem, Select, SelectChangeEvent, FormControl, IconButton } from '@mui/material';
import { useState, useEffect, useRef } from 'react';
import { PROPERTY_TYPE_GROUPS } from 'util/Constants';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import CloseIcon from '@mui/icons-material/Close';

interface DropdownProps {
    title?: string;
    description?: string;
    Icon?: React.FC<React.SVGProps<SVGSVGElement>>;
    options: (string | number)[];
    isShown?: boolean;
    heightClass?: string;
    isEditMode?: boolean;
    widthClass?: string;
    value?: string | number;
    onChange?: (value: string | number) => void;
    showClearButton?: boolean;
    multiple?: boolean;
    allowAdd?: boolean;
}

export default function Dropdown({
    title,
    description,
    Icon,
    options,
    isShown = true,
    heightClass = 'h-12',
    isEditMode,
    widthClass = '',
    value,
    onChange,
    showClearButton = true,
    multiple = false,
    allowAdd = false,
}: DropdownProps) {
    const [searchTerm, setSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [selectedValues, setSelectedValues] = useState<string[]>([]);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const [displayValue, setDisplayValue] = useState<string | number>('');

    // Update the display value when the external value changes
    useEffect(() => {
        if (value !== undefined && value !== null) {
            setDisplayValue(value);
        } else {
            setDisplayValue('');
        }
    }, [value]);

    useEffect(() => {
        if (multiple && typeof value === 'string') {
            setSelectedValues(value.split(', ').filter(Boolean));
        }
    }, [value, multiple]);

    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [isOpen]);

    const handleChange = (event: SelectChangeEvent<string | number | string[]>) => {
        const selectedValue = event.target.value;

        if (multiple) {
            const newSelectedValues = Array.isArray(selectedValue) ? selectedValue : [selectedValue as string];
            setSelectedValues(newSelectedValues);
            onChange?.(newSelectedValues.join(', '));
        } else {
            setDisplayValue(selectedValue as string | number);
            onChange?.(selectedValue as string | number);
        }
    };

    const handleGroupClick = (event: React.MouseEvent, group: { name: string; types: string[] }) => {
        event.preventDefault();
        event.stopPropagation();

        const allTypesSelected = group.types.every((type) => selectedValues.includes(type));
        let newSelectedValues: string[];

        if (allTypesSelected) {
            newSelectedValues = selectedValues.filter((value) => !group.types.includes(value));
        } else {
            newSelectedValues = Array.from(new Set([...selectedValues, ...group.types]));
        }

        setSelectedValues(newSelectedValues);
        onChange?.(newSelectedValues.join(', '));
    };

    const handleTypeClick = (event: React.MouseEvent, type: string) => {
        event.preventDefault();
        event.stopPropagation();

        const isSelected = selectedValues.includes(type);
        const newSelectedValues = isSelected ? selectedValues.filter((value) => value !== type) : [...selectedValues, type];

        setSelectedValues(newSelectedValues);
        onChange?.(newSelectedValues.join(', '));
    };

    const handleClear = () => {
        setSelectedValues([]);
        setDisplayValue('');
        onChange?.('');
    };

    const handleAddNew = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (searchTerm.trim() && allowAdd) {
            const newValue = searchTerm.trim();
            // Update the display value immediately
            setDisplayValue(newValue);
            // Call the onChange handler
            onChange?.(newValue);
            // Reset search term
            setSearchTerm('');
            // Close the dropdown
            setIsOpen(false);
        }
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setSearchTerm(e.target.value);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        e.stopPropagation();

        if (e.key === 'Enter' && allowAdd && searchTerm.trim() && filteredOptions.length === 0) {
            e.preventDefault();

            const newValue = searchTerm.trim();
            // Update the display value immediately
            setDisplayValue(newValue);
            // Call the onChange handler
            onChange?.(newValue);
            // Reset search term
            setSearchTerm('');
            // Close the dropdown
            setIsOpen(false);
        }
    };

    const filteredOptions = options.filter((option) => {
        const optionStr = String(option).toLowerCase();
        return optionStr.includes(searchTerm.toLowerCase());
    });

    const renderValue = (selected: string | number | string[]) => {
        if (multiple) {
            return selectedValues.join(', ');
        }
        if (!selected || selected === '') {
            return 'Select an option';
        }
        // Use our internal displayValue state for rendering
        return displayValue || selected;
    };

    const isFilterAdded = Boolean(value);

    // Convert string value to array for multiple select
    const selectValue = multiple ? selectedValues : displayValue || '';

    return (
        <div
            className={`${isEditMode ? 'w-full' : widthClass || 'lg:w-1/2 w-full'} relative ${isShown ? 'p-5 border rounded-lg' : ''} ${isFilterAdded && isShown ? 'border-my-blue border-2' : 'border-gray-300'}`}
        >
            <FormControl size="medium" className="bg-white/20 w-full">
                {isShown && (
                    <div className="font-poppins">
                        <div className="flex gap-2 py-2 mt-2">
                            {Icon && <Icon className="h-6 w-6" />}
                            <p className="font-medium text-my-black/80">{title}</p>
                        </div>
                        <h3 className="my-2 text-my-black/50 font-normal text-[12px]">{description}</h3>
                    </div>
                )}
                <Select
                    multiple={multiple}
                    value={selectValue}
                    onChange={handleChange}
                    onOpen={() => setIsOpen(true)}
                    onClose={() => {
                        setIsOpen(false);
                        setSearchTerm('');
                    }}
                    renderValue={renderValue}
                    className={heightClass}
                    displayEmpty
                    MenuProps={{
                        PaperProps: {
                            style: {
                                maxHeight: 300,
                                marginTop: 10,
                            },
                        },
                    }}
                >
                    {(isShown || allowAdd) && (
                        <div className="sticky top-0 bg-white p-2 border-b z-10">
                            <div className="relative">
                                <SearchOutlinedIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    placeholder="Search..."
                                    className="w-full pl-8 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    onKeyDown={handleKeyDown}
                                    autoFocus
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </div>
                        </div>
                    )}

                    {displayValue && (
                        <MenuItem
                            onClick={handleClear}
                            sx={{
                                cursor: 'pointer',
                                color: '#ef4444',
                                fontWeight: 500,
                                '&:hover': {
                                    backgroundColor: 'rgba(239, 68, 68, 0.04)',
                                },
                            }}
                        >
                            <ListItemText
                                primary="Clear Selection"
                                sx={{
                                    m: 0,
                                    '& .MuiTypography-root': {
                                        fontSize: '14px',
                                        color: '#ef4444',
                                        fontWeight: 500,
                                    },
                                }}
                            />
                        </MenuItem>
                    )}

                    {multiple &&
                        PROPERTY_TYPE_GROUPS.map((group) => (
                            <div key={group.name}>
                                <MenuItem
                                    onClick={(e) => handleGroupClick(e, group)}
                                    sx={{
                                        cursor: 'pointer',
                                        '&:hover': {
                                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                        },
                                    }}
                                >
                                    <Checkbox
                                        checked={group.types.every((v) => selectedValues.includes(v))}
                                        indeterminate={
                                            group.types.some((v) => selectedValues.includes(v)) && !group.types.every((v) => selectedValues.includes(v))
                                        }
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleGroupClick(e, group);
                                        }}
                                        sx={{
                                            padding: '0 8px 0 0',
                                            color: '#1976d2',
                                            '&.Mui-checked': {
                                                color: '#1976d2',
                                            },
                                            '&:hover': {
                                                backgroundColor: 'transparent',
                                            },
                                            width: '16px',
                                            height: '16px',
                                            '& .MuiSvgIcon-root': {
                                                fontSize: '16px',
                                            },
                                        }}
                                    />
                                    <ListItemText
                                        primary={group.name}
                                        sx={{
                                            m: 0,
                                            '& .MuiTypography-root': {
                                                fontSize: '14px',
                                                fontWeight: 600,
                                            },
                                        }}
                                    />
                                </MenuItem>
                                {group.types.map((type) => (
                                    <MenuItem
                                        key={type}
                                        onClick={(e) => handleTypeClick(e, type)}
                                        sx={{
                                            pl: 4,
                                            cursor: 'pointer',
                                            '&:hover': {
                                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                            },
                                        }}
                                    >
                                        <Checkbox
                                            checked={selectedValues.includes(type)}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleTypeClick(e, type);
                                            }}
                                            sx={{
                                                padding: '0 8px 0 0',
                                                color: '#1976d2',
                                                '&.Mui-checked': {
                                                    color: '#1976d2',
                                                },
                                                '&:hover': {
                                                    backgroundColor: 'transparent',
                                                },
                                                width: '16px',
                                                height: '16px',
                                                '& .MuiSvgIcon-root': {
                                                    fontSize: '16px',
                                                },
                                            }}
                                        />
                                        <ListItemText
                                            primary={type}
                                            sx={{
                                                m: 0,
                                                '& .MuiTypography-root': {
                                                    fontSize: '14px',
                                                },
                                            }}
                                        />
                                    </MenuItem>
                                ))}
                            </div>
                        ))}

                    {!multiple &&
                        filteredOptions.map((option) => (
                            <MenuItem key={option} value={option}>
                                <ListItemText primary={option} sx={{ m: 0, '& .MuiTypography-root': { fontSize: '14px' } }} />
                            </MenuItem>
                        ))}

                    {allowAdd && !multiple && filteredOptions.length === 0 && searchTerm.trim() !== '' && (
                        <MenuItem
                            onClick={handleAddNew}
                            sx={{
                                cursor: 'pointer',
                                color: '#1976d2',
                                fontWeight: 500,
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                },
                            }}
                        >
                            <ListItemText
                                primary={`Add "${searchTerm.trim()}"`}
                                sx={{
                                    m: 0,
                                    '& .MuiTypography-root': {
                                        fontSize: '14px',
                                        color: '#1976d2',
                                        fontWeight: 500,
                                    },
                                }}
                            />
                        </MenuItem>
                    )}
                </Select>
            </FormControl>
        </div>
    );
}
